import type { PinCreateRequest, PinView } from "@screener/protocol";
import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";

/**
 * HTTP-клиент закреплений §6.7 (1.18) запросного канала §7.6. Маршруты —
 * `/api/pins` и `/api/pins/{id}`; тот же стиль различения исходов статусом,
 * что у `notes-api.ts` и `density-api.ts`.
 */

const PINS_BASE = "/api/pins";

function isPinView(value: unknown): value is PinView {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    isRecord(value.instrument) &&
    typeof value.instrument.exchange === "string" &&
    typeof value.instrument.market === "string" &&
    typeof value.instrument.base === "string" &&
    typeof value.instrument.quote === "string" &&
    (value.side === "bid" || value.side === "ask") &&
    typeof value.price === "string" &&
    typeof value.created_at_ms === "number"
  );
}

/** Список закреплений §6.7 предъявившего сессию пользователя, целиком. */
export type PinsListResult =
  | { status: "ok"; pins: PinView[] }
  | { status: "unauthorized" }
  | { status: "unavailable" };

export async function fetchPins(): Promise<PinsListResult> {
  let response: Response;
  try {
    response = await fetch(PINS_BASE, { signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS) });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (!response.ok) {
    console.error("список закреплений отклонён демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!Array.isArray(body) || !body.every(isPinView)) {
      return { status: "unavailable" };
    }
    return { status: "ok", pins: body };
  } catch {
    return { status: "unavailable" };
  }
}

/**
 * Исход закрепления уровня. Демон отвечает `200`, не `201`: повторное
 * закрепление того же уровня идемпотентно и не считается вторым созданием
 * (design.md решение 3) — клиенту незачем различать эти два случая статусом.
 */
export type PinCreateResult =
  | { status: "ok"; pin: PinView }
  | { status: "unauthorized" }
  | { status: "invalid" }
  | { status: "unavailable" };

export async function createPin(request: PinCreateRequest): Promise<PinCreateResult> {
  let response: Response;
  try {
    response = await fetch(PINS_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 400) {
    return { status: "invalid" };
  }
  if (!response.ok) {
    console.error("закрепление отклонено демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!isPinView(body)) {
      return { status: "unavailable" };
    }
    return { status: "ok", pin: body };
  } catch {
    return { status: "unavailable" };
  }
}

/**
 * Исход снятия закрепления. Демон не различает неизвестный идентификатор и
 * чужое закрепление (§6.7) — оба приходят `404`, и клиент называет оба «нет
 * такого».
 */
export type PinDeleteResult =
  | { status: "ok" }
  | { status: "unauthorized" }
  | { status: "not-found" }
  | { status: "unavailable" };

export async function deletePin(id: string): Promise<PinDeleteResult> {
  let response: Response;
  try {
    response = await fetch(`${PINS_BASE}/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 404) {
    return { status: "not-found" };
  }
  if (response.status === 204) {
    return { status: "ok" };
  }
  console.error("закрепление не снято демоном", response.status);
  return { status: "unavailable" };
}
